import AuthContext from 'contexts/AuthContext';
import FoodUpdater from 'data/FoodUpdater';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import type { DropdownItemProps, DropdownProps } from 'semantic-ui-react';
import { Button, Card, Dropdown, Loader, Message, Modal, Table } from 'semantic-ui-react';
import type { FoodOrder, FoodUpdaterResponse, OrderPayment } from 'types';

import * as prohealth from './prohealth-api';


/**
 * One row of the past-payments table: a Solutran payment, collapsed across every
 * order it settled. Built by folding `paidOrders[].payments[]` on `paymentId`.
 *
 * Every field is copied straight off an {@link OrderPayment}, so the four that
 * are optional there are `| undefined` here. They are declared present-but-
 * possibly-undefined rather than optional because `refreshData` assigns each
 * one unconditionally when it builds the row.
 */
type PastPayment = {
  id: string | undefined;
  status: string;
  date: string;
  type: string | undefined;
  amount: string | undefined;
  authCode: string | undefined;
  productName: string | undefined;
  orders: { id: string | undefined; status: string | undefined }[];
};

/**
 * A dropdown option as this file builds them, which is not `DropdownItemProps`.
 *
 * Options are `{ key: x, text: x, value: x }` from an optional payment or order
 * id, so all three fields are present and may hold `undefined`.
 * `DropdownItemProps` declares them as plain optionals, which under
 * `exactOptionalPropertyTypes` rejects exactly what this code produces.
 *
 * Stated as its own type rather than asserted: the arrays really do carry
 * `undefined` entries at runtime (see the blank-options defect note in
 * `makePayment.tsx`).
 */
type PaymentDropdownOption = {
  key: string | undefined;
  text: string | undefined;
  value: string | undefined;
};

/**
 * What the past-payments table accepts.
 *
 * Established from its only call site, `./index.tsx:60`, and cross-checked
 * against the spec's `makeProps` (`pastPayments.spec.tsx:61`), which builds the
 * same five fields.
 *
 * `paidOrders` is `FoodOrder[] | undefined`: the caller derives it as
 * `props.ordersData?.filter(o => o.payments)`, and `ordersData` is SWR 0.5.7's
 * optional `data`, so the whole expression is absent on the first render. The
 * `props.paidOrders?.forEach` at line 63 already reflects that; the two
 * unguarded reads at lines 154 and 161 do not — see the SUSPECTED DEFECT note
 * on `voidTransaction`.
 */
type PastPaymentsProps = {
  paidOrders?: FoodOrder[] | undefined;
  /** SWR's revalidate; awaited only for completion, never for its value. */
  ordersMutate: () => Promise<unknown>;
  setView: (view: null) => void;
  /**
   * The order id the Orders tab routed in with; seeds the order filter. Read
   * once as the `useState` initialiser.
   *
   * Explicitly `| undefined`: the caller passes it from a `useState<string>()`
   * that is absent until a row routes here, which under
   * `exactOptionalPropertyTypes` a bare `?` would reject.
   */
  solutranPastPaymentsSelectedOrder?: string | null | undefined;
  setSolutranPastPaymentsSelectedOrder: (orderId: null) => void;
};

export default function PastPayments(props: PastPaymentsProps) {
  /*
   * Read through the raw context, not the `useAuthContext()` accessor, and
   * non-null asserted. The accessor throws when the provider is missing; this
   * component does not, and the spec renders it bare without
   * `renderWithProviders`. Switching to the accessor would make those cases throw.
   */
  const authInfo = useContext(AuthContext)!;

  // The three cleared with an explicit `setX(null)` carry `null` in their type.
  const [loader, setLoader] = useState<string | null>();
  const [success, setSuccess] = useState<string | null>();
  const [error, setError] = useState<string | null>();
  const [modal, setModal] = useState(false);
  const [payments, setPayments] = useState<PastPayment[]>();
  const [paymentIdOptions, setPaymentIdOptions] = useState<PaymentDropdownOption[]>([]);
  // The three filters hold the chosen dropdown value, or `null` once cleared.
  const [paymentIdFilter, setPaymentIdFilter] = useState<string | null>();
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string | null>();
  const [orderIdOptions, setOrderIdOptions] = useState<PaymentDropdownOption[]>();
  const [orderIdFilter, setOrderIdFilter] = useState<string | null | undefined>(props.solutranPastPaymentsSelectedOrder);

  const paymentStatusOptions = [
    { key: 1, text: 'paid', value: 'paid' },
    { key: 2, text: 'cancelled', value: 'cancelled' },
    { key: 3, text: 'rejected', value: 'rejected' },
  ];

  const refreshData = () => {
    setLoader('Fetching orders data...');
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection leaves the loader spinning with no error shown
    props.ordersMutate().then(() => {
      setLoader(null);

      setOrderIdOptions(props.paidOrders?.map((o) => ({ key: o.orderId, text: o.orderId, value: o.orderId })));

      const paymentsArr: PastPayment[] = [];
      const paymentsIdArr: (string | undefined)[] = [];
      props.paidOrders?.forEach((o) => {
        // `payments` is optional on `FoodOrder` but the caller has already
        // filtered to orders that carry it (`index.tsx:61`), so the unguarded
        // read is sound here — unlike the same read at line 155.
        o.payments!.forEach((p) => {
          const i = paymentsArr.findIndex((a) => a.id === p.paymentId);
          if (i >= 0) {
            // `findIndex` returned a hit, so the element exists; the assertions
            // express that rather than re-checking it.
            paymentsArr[i]!.orders.push({ id: o.orderId, status: o.status });
            paymentsArr[i]!.status = p.paymentStatus;
            paymentsArr[i]!.date = p.paymentDate;
            paymentsArr[i]!.amount = p.paymentStatus === 'paid' ? p.paymentAmount : '0';
          } else {
            paymentsArr.push({
              id: p.paymentId,
              status: p.paymentStatus,
              date: p.paymentDate,
              type: p.paymentType,
              amount: p.paymentAmount,
              authCode: p.paymentAuthCode,
              productName: p.paymentProductName,
              orders: [{ id: o.orderId, status: o.status }],
            });
          }

          paymentsIdArr.push(p.paymentId);
        });
      });

      const uniquePaymentsIdArr = paymentsIdArr.filter((item, pos, self) => self.indexOf(item) === pos);
      const uniquePaymentsIdOptions = uniquePaymentsIdArr.map((i) => ({ key: i, text: i, value: i }));

      setPayments(paymentsArr);
      setPaymentIdOptions(uniquePaymentsIdOptions);
    });
  };

  useEffect(() => {
    refreshData();
  }, []);

  // See makePayment.tsx for why `value.value` narrows to `string` by assertion:
  // all three dropdowns here are single-select over string-valued options.
  const handleFilterChange = (e: React.SyntheticEvent<HTMLElement>, value: DropdownProps, filter: string) => {
    if (value.value === '') {
      props.setView(null);
      props.setSolutranPastPaymentsSelectedOrder(null);
    }
    if (filter === 'paymentId') setPaymentIdFilter(value.value === '' ? null : (value.value as string));
    if (filter === 'paymentStatus') setPaymentStatusFilter(value.value === '' ? null : (value.value as string));
    if (filter === 'orderId') setOrderIdFilter(value.value === '' ? null : (value.value as string));
  };

  const matchesFilters = (
    paymentId: string | undefined,
    paymentStatus: string,
    orders: PastPayment['orders']
  ) => {
    let match = true;
    if (paymentIdFilter && paymentId !== paymentIdFilter) {
      match = false;
    }
    if (paymentStatusFilter && paymentStatus !== paymentStatusFilter) {
      match = false;
    }
    if (orderIdFilter && !orders.find((o) => o.id === orderIdFilter)) {
      match = false;
    }
    return match;
  };

  // Only called from inside the `payments && …` JSX branch, so the state is
  // populated by the time this runs; the assertions record that rather than
  // adding a check the code does not perform.
  const displayVoidTransaction = (paymentId: string | undefined) => {
    let bool = true;
    for (let i = 0; i < payments!.length; i++) {
      if (payments![i]!.id === paymentId) {
        if (payments![i]!.status !== 'paid') {
          bool = false;
          break;
        }
        for (let j = 0; j < payments![i]!.orders.length; j++) {
          if (payments![i]!.orders[j]!.status !== 'new') {
            bool = false;
            break;
          }
        }
      }
    }
    return bool;
  };

  /*
   * Each parameter is `string | undefined` because the JSX passes the row's
   * fields straight through (`voidTransaction(p.date, p.id, p.authCode,
   * p.productName)`), and three of those are optional on {@link OrderPayment}.
   * `prohealth.voidTransaction` declares all three as required `string`, so the
   * assertions at the call below are where the two sides disagree.
   *
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. `props.paidOrders` is read unguarded
   * at lines 154 and 161, and `o.payments` at line 155, after the same values
   * were read with `?.` at lines 59 and 63. If the void succeeds while
   * `paidOrders` is absent — which it is for the whole first render, and again
   * whenever `ordersMutate` resolves to nothing — this throws *after* the
   * transaction has already been voided at the processor, leaving the orders
   * marked paid in CareBetty against a payment that no longer exists.
   */
  const voidTransaction = async (
    paymentDate: string,
    paymentId: string | undefined,
    paymentAuthCode: string | undefined,
    paymentProductName: string | undefined
  ) => {
    setSuccess(null);
    setError(null);
    setLoader('Voiding transaction...');
    await prohealth.voidTransaction(authInfo.token, paymentDate, paymentId!, paymentAuthCode!).then(
      // Declared async so callers get a promise; removing async changes the return type from a
      // promise to a value.
      // eslint-disable-next-line @typescript-eslint/require-await
      async (response) => {
        if (response.data.status === 0) {
          setSuccess('Transaction successfully voided!');
          setLoader('Updating order info on CareBetty...');

          const orderIds: (string | undefined)[] = [];
          props.paidOrders!.forEach((o) => {
            if (o.payments!.find((p) => p.paymentId === paymentId)) {
              orderIds.push(o.orderId);
            }
          });

          // Async callback passed where a void return is expected, as upstream writes it; wrapping
          // it would emit a new function.
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          orderIds.forEach(async (o) => {
            const order = props.paidOrders!.find((l) => l.orderId === o)!;
            setLoader('Updating order info on CareBetty...');
            // `data` asserted as in makePayment — see the envelope note in
            // ./prohealth-api.ts.
            const newPayment: OrderPayment = {
              paymentStatus: 'cancelled',
              paymentDate: moment().utc().format(),
              paymentId: response.data.data!.orderId,
              paymentAuthCode: response.data.data!.authorizeCode,
              paymentType: 'solutran',
              paymentAmount: response.data.data!.authorizeAmount,
              paymentProductName: paymentProductName,
            };
            order['lastUpdated'] = moment().utc().format();
            order['lastUpdatedBy'] = authInfo.practitionerName;
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- ternary used as a statement; the `if` that satisfies the rule is a different expression
            order['payments'] ? order['payments'].push(newPayment) : (order['payments'] = [newPayment]);

            await FoodUpdater(`/order/` + order.orderId, authInfo.token, order).then(
              (response: FoodUpdaterResponse) => {
                setLoader(null);
                if (response.statusCode === '200') {
                  setModal(true);
                  props.setView(null);
                  setError(null);
                  refreshData();
                } else {
                  setError('ERROR with updating order - ' + response.message);
                }
              },
              (error) => {
                setLoader(null);
                setError('ERROR with updating order - ' + error);
              }
            );
          });
        } else {
          setLoader(null);
          setError(response.data.message ?? 'ERROR: could not void transaction.');
        }
      },
      (error: Error) => {
        setLoader(null);
        setError(error.message);
      }
    );
  };

  return (
    <>
      {loader && (
        <Card>
          <Loader active inline content={loader} />
        </Card>
      )}
      {success && (
        <Card>
          <Message success size="small" content={success} />
        </Card>
      )}
      {error && (
        <Card>
          <Message error size="small" header={'Error!'} content={error} />
        </Card>
      )}

      {payments && (
        <>
          {/*
            * The `value=` on all three filter Dropdowns below is a standing
            * TS2412 and is left as written. Each filter holds
            * `string | null | undefined` — `undefined` before any choice, `null`
            * after the clearable X — while `DropdownProps['value']` admits
            * neither. Semantic reads `undefined` as uncontrolled and `null` is
            * outside its declared union entirely, so any coercion here would
            * change how these controls behave on first render or after
            * clearing. A genuine disagreement between this component's state
            * model and the library's prop type, reported rather than papered over.
            *
            * The `options=` on the first and third carry the matching TS2322/TS2412
            * for the same underlying reason: those lists are built from optional
            * payment and order ids, so their entries really do hold `undefined`
            * (see {@link PaymentDropdownOption}). Coercing them to
            * `DropdownItemProps[]` would assert a well-formedness the data does
            * not have and would hide the blank-option defect noted in
            * `makePayment.tsx`.
            */}
          <Dropdown
            placeholder="Filter Payment ID"
            clearable
            selection
            search
            value={paymentIdFilter!}
            options={paymentIdOptions as DropdownItemProps[]}
            onChange={(e, value) => handleFilterChange(e, value, 'paymentId')}
          />
          <Dropdown
            placeholder="Filter Payment Status"
            clearable
            selection
            search
            value={paymentStatusFilter!}
            options={paymentStatusOptions}
            onChange={(e, value) => handleFilterChange(e, value, 'paymentStatus')}
          />
          <Dropdown
            placeholder="Filter Order ID"
            clearable
            selection
            search
            value={orderIdFilter!}
            options={orderIdOptions as DropdownItemProps[]}
            onChange={(e, value) => handleFilterChange(e, value, 'orderId')}
          />

          <Table celled striped sortable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Payment ID</Table.HeaderCell>
                <Table.HeaderCell>Payment Status</Table.HeaderCell>
                <Table.HeaderCell>Payment Date</Table.HeaderCell>
                <Table.HeaderCell>Payment Type</Table.HeaderCell>
                <Table.HeaderCell>Payment Amount</Table.HeaderCell>
                <Table.HeaderCell>Order ID(s)</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {payments
                .filter((p) => matchesFilters(p.id, p.status, p.orders))
                .map((p) => (
                  <Table.Row key={p.id}>
                    <Table.Cell>{p.id}</Table.Cell>
                    <Table.Cell>{p.status}</Table.Cell>
                    <Table.Cell>{p.date}</Table.Cell>
                    <Table.Cell>{p.type}</Table.Cell>
                    <Table.Cell>{p.amount}</Table.Cell>
                    <Table.Cell>
                      {p.orders
                        .map((o) => o.id)
                        .filter((item, pos, self) => self.indexOf(item) === pos)
                        .join(', ')}
                    </Table.Cell>
                    <Table.Cell>
                      {displayVoidTransaction(p.id) && (
                        <Button
                          content="Void Transaction"
                          onClick={() => {
                            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget on a payment action; a rejection is dropped after the card may already be charged
                            voidTransaction(p.date, p.id, p.authCode, p.productName);
                          }}
                        />
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
            </Table.Body>
          </Table>
        </>
      )}

      {
        <Modal onClose={() => setModal(false)} onOpen={() => setModal(true)} open={modal} size="tiny">
          <Modal.Header>Reminder</Modal.Header>
          <Modal.Content>
            <Modal.Description>Please remember to cancel the order on the Orders tab if necessary!</Modal.Description>
          </Modal.Content>
          <Modal.Actions>
            <Button content="Close" labelPosition="right" icon="x" negative onClick={() => setModal(false)} />
          </Modal.Actions>
        </Modal>
      }
    </>
  );
}
